import { ChevronRight, Folder } from "lucide-react";
import type { FC } from "react";
import React from "react";

import { getUserPresentableMessage } from "@core/foundation/error";
import { bindArgs } from "@core/foundation/function";

import { EmptyView, ErrorView } from "@react/foundation/placeholder";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@react/ui-primitives/collapsible";
import {
  SidebarMenu,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@react/ui-primitives/sidebar";
import { Skeleton } from "@react/ui-primitives/skeleton";

import type { FolderTreeNode } from "@apis/taxonomy/api";

interface FolderTreeProps {
  readonly error: Error | null;
  readonly folders: readonly FolderTreeNode[];
  readonly isPending: boolean;
  readonly onSelect: (folder: FolderTreeNode) => void;
  readonly selected: FolderTreeNode | null;
  readonly skeletonCount?: number;
}

export const FolderTree: FC<FolderTreeProps> = ({
  error,
  folders,
  isPending,
  onSelect,
  selected,
  skeletonCount = 4,
}) => {
  if (isPending) {
    return (
      <div className="flex flex-col gap-1">
        {Array.from({ length: skeletonCount }, (_, index) => (
          <Skeleton className="h-7" key={index} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <ErrorView
        description={getUserPresentableMessage(error)}
        title="Failed to load folders"
      />
    );
  }

  if (folders.length === 0) {
    return (
      <EmptyView
        description="Ask your agent to set the taxonomy's folder structure."
        title="No folders yet"
      />
    );
  }

  return (
    <SidebarMenu>
      {folders.map((folder, index) => (
        <FolderTreeItem
          folder={folder}
          key={index}
          onSelect={onSelect}
          selected={selected}
        />
      ))}
    </SidebarMenu>
  );
};

interface FolderTreeItemProps {
  readonly folder: FolderTreeNode;
  readonly onSelect: (folder: FolderTreeNode) => void;
  readonly selected: FolderTreeNode | null;
}

const FolderTreeItem: FC<FolderTreeItemProps> = ({
  folder,
  onSelect,
  selected,
}) => {
  const button = (
    <SidebarMenuSubButton asChild isActive={folder === selected}>
      <button onClick={bindArgs(onSelect, folder)} type="button">
        <ChevronRight
          className={`
            transition-transform
            group-data-[state=open]/collapsible:rotate-90
            ${folder.subfolders.length === 0 ? "invisible" : ""}
          `}
        />
        <Folder />
        <span>{folder.name}</span>
      </button>
    </SidebarMenuSubButton>
  );

  if (folder.subfolders.length === 0) {
    return <SidebarMenuSubItem>{button}</SidebarMenuSubItem>;
  }

  return (
    <SidebarMenuSubItem>
      <Collapsible className="group/collapsible">
        <CollapsibleTrigger asChild>{button}</CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {folder.subfolders.map((subfolder, index) => (
              <FolderTreeItem
                folder={subfolder}
                key={index}
                onSelect={onSelect}
                selected={selected}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuSubItem>
  );
};
